import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AuthLayout, AuthLink } from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    tenant_name: "",
    tenant_slug: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    full_name: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signup({
        ...form,
        tenant_slug: form.tenant_slug.trim().toLowerCase().replace(/\s+/g, "-"),
        email: form.email.trim(),
      });
      navigate("/app/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Create your workspace"
      subtitle="Two minutes. Your workspace, your first admin account, and you're in."
      footer={<>Already have an account? <AuthLink to="/login" data-testid="link-login">Sign in</AuthLink>.</>}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="tenant_name">Workspace name</Label>
            <Input id="tenant_name" required value={form.tenant_name} onChange={set("tenant_name")} placeholder="Acme Construction" data-testid="signup-tenant-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenant_slug">Workspace URL</Label>
            <Input id="tenant_slug" required value={form.tenant_slug} onChange={set("tenant_slug")} placeholder="acme" data-testid="signup-tenant-slug" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <Input id="timezone" value={form.timezone} onChange={set("timezone")} data-testid="signup-timezone" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="full_name">Your name</Label>
          <Input id="full_name" required value={form.full_name} onChange={set("full_name")} placeholder="Casey Founder" data-testid="signup-full-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="signup-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password <span className="text-muted-foreground">(≥8 chars)</span></Label>
          <Input id="password" type="password" minLength={8} required value={form.password} onChange={set("password")} data-testid="signup-password" />
        </div>
        {error && <Alert variant="destructive" data-testid="signup-error"><AlertDescription>{error}</AlertDescription></Alert>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="signup-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create workspace
        </Button>
      </form>
    </AuthLayout>
  );
}
